// Each main lift's best estimated max BEFORE a session (walkthrough 7's
// "any estimated-max best", W16), from GET summary/ — which keeps the best
// estimate per session for the main lifts over the last 26 weeks. Kept out
// of the component file (react-refresh).
import { useEffect, useState } from "react";
import { trainingApi } from "../../../services/api";

/** How far back summary/ looks (backend/training/engine_constants.py). */
export const BEST_WEEKS = 26;

/**
 * `Map(exercise → {e1rm, date} | null)` for the lifts summary/ tracks: the
 * best estimate of the sessions before `session` (null: none in the 26
 * weeks — today's is the first). A lift summary/ doesn't track, or one it
 * can't place (`past`: this session not in its trend, e.g. older than 26
 * weeks), isn't in the map: nothing is claimed for it. Nor, `past`, is one
 * with no session before it in the trend: the 26 weeks end today, not that
 * day, so it may have been done in the weeks before it — no "first".
 *
 * Before: the trend's entries ahead of this session's own (oldest first).
 * Not in the trend yet (its sets still on their way) and not `past`: all of
 * them — they're all earlier.
 */
export function pastBests(summary, session, { past = false } = {}) {
  const out = new Map();
  for (const lift of summary?.main_lifts ?? []) {
    const trend = lift.trend ?? [];
    const at = trend.findIndex((point) => point.session === session?.id);
    let before;
    if (at >= 0) before = trend.slice(0, at);
    else if (!past) before = trend;
    else continue;
    const best = before.reduce((top, point) => (top === null || point.e1rm > top.e1rm ? point : top), null);
    if (best === null && past) continue;
    out.set(lift.exercise, best ? { e1rm: best.e1rm, date: best.date } : null);
  }
  return out;
}

/**
 * What a lift's estimate today says against `before` (a pastBests value,
 * or undefined when untracked): "best" (it beats every earlier session's —
 * compared at summary/'s one decimal), "first", or null. A best set logged
 * without its reserve claims nothing: its estimate is a guess, and summary/
 * leaves such sets out.
 */
export function bestVerdict(best, before) {
  if (before === undefined || best?.set?.rir === null || best?.set?.rir === undefined) return null;
  if (before === null) return "first";
  return Math.round(best.e1rm * 10) / 10 > before.e1rm ? "best" : null;
}

/**
 * GET summary/ once per session: `{status, bests}` — status "loading",
 * "ready", "failed", or "units" (summary/ is in other units than the
 * session's: not compared). `enabled` false (no main-lift estimate to
 * compare): nothing is asked — summary/ is a heavy read — and the status
 * is "idle".
 */
export function usePastBests(session, { past = false, enabled = true } = {}) {
  const id = session?.id;
  const units = session?.units ?? "lb";
  const [state, setState] = useState({ id, status: "loading", data: null });
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    setState({ id, status: "loading", data: null });
    (async () => {
      try {
        const { data } = await trainingApi.summary();
        if (alive) setState({ id, status: "ready", data });
      } catch {
        if (alive) setState({ id, status: "failed", data: null });
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, enabled]);
  if (!enabled) return { status: "idle", bests: new Map(), units: null };
  // An answer for another session (the id changed) isn't this one's. (No
  // test can fail on the id check: the effect resets the state in the same
  // act() a test's rerender runs in — it covers the paint in between.)
  if (state.id !== id || state.status !== "ready") {
    return { status: state.id === id ? state.status : "loading", bests: new Map(), units: null };
  }
  const theirs = state.data?.units ?? "lb";
  if (theirs !== units) return { status: "units", bests: new Map(), units: theirs };
  return { status: "ready", bests: pastBests(state.data, session, { past }), units: theirs };
}
